import React, { useState, useEffect, useCallback } from 'react';
import { getLeads } from '../api/leadsApi';
import LeadDetail from '../components/LeadDetail';
import UpworkBadge from '../components/UpworkBadge';
import { STAGES } from './PipelinePage';

const STAGE_MAP = STAGES.reduce((acc, st) => { acc[st.key] = st; return acc; }, {});
const DEAL_LABELS = { small: '< $5k', medium: '$5k–$20k', large: '$20k–$50k', enterprise: '$50k+' };
const SOURCE_LABELS = { upwork: 'Upwork', client_referral: 'Client Ref.', personal_referral: 'Personal Ref.', linkedin: 'LinkedIn', inbound: 'Inbound', other: 'Other' };
const SOURCE_COLORS = { upwork: '#14a800', client_referral: '#534AB7', personal_referral: '#0F6E56', linkedin: '#0077b5', inbound: '#BA7517', other: '#888' };
const SOURCE_OPTIONS = Object.keys(SOURCE_LABELS);

export default function LeadsPage() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSource) params.source = filterSource;
      if (search) params.search = search;
      if (filterStage) params.stage = filterStage;
      const res = await getLeads(params);
      // Sort by actual Upwork conversation recency where available (a bulk sync
      // sweep processes rooms newest-first, so DB insertion order alone would
      // put the oldest conversation's lead at the top), falling back to
      // creation time for leads from other sources.
      const recency = (lead) => new Date(lead.upworkLastSyncedAt || lead.createdAt || 0).getTime();
      const sorted = [...res.data].sort((a, b) => recency(b) - recency(a));
      setLeads(sorted);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, filterStage, filterSource]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Leads</h1>
          <p style={s.sub}>All leads across every source</p>
        </div>
      </div>

      <div style={s.toolbar}>
        <input style={s.search} placeholder="Search by name, company, email..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={s.select} value={filterSource} onChange={e => setFilterSource(e.target.value)}>
          <option value="">All sources</option>
          {SOURCE_OPTIONS.map(src => <option key={src} value={src}>{SOURCE_LABELS[src]}</option>)}
        </select>
        <select style={s.select} value={filterStage} onChange={e => setFilterStage(e.target.value)}>
          <option value="">All stages</option>
          {STAGES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
        </select>
      </div>

      <div style={s.table}>
        <div style={s.tableHeader}>
          <span>Name</span><span>Company</span><span>Source</span><span>Stage</span><span>Deal size</span><span>Assigned to</span><span>Last synced</span>
        </div>
        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : leads.length === 0 ? (
          <div style={s.empty}>No leads match these filters.</div>
        ) : leads.map(lead => {
          const stage = STAGE_MAP[lead.stage];
          const lastSynced = lead.upworkLastSyncedAt || lead.createdAt;
          return (
            <div key={lead._id} style={s.tableRow} onClick={() => setSelectedLead(lead)}>
              <span style={s.nameCell}>
                {lead.source === 'upwork' && <UpworkBadge size={13} style={{ marginRight: 6 }} />}
                {lead.name}
              </span>
              <span style={s.cell}>{lead.company}</span>
              <span>
                <span style={{ ...s.badge, background: (SOURCE_COLORS[lead.source] || '#888') + '18', color: SOURCE_COLORS[lead.source] || '#888' }}>
                  {SOURCE_LABELS[lead.source] || lead.source}
                </span>
              </span>
              <span>
                <span style={{ ...s.badge, background: stage?.bg || '#f0efe9', color: stage?.color || '#666' }}>
                  {stage?.label || lead.stage}
                </span>
              </span>
              <span style={s.cell}>{DEAL_LABELS[lead.dealSize] || '—'}</span>
              <span style={s.cell}>{lead.assignedToName || 'Unassigned'}</span>
              <span style={s.cell}>{lastSynced ? new Date(lastSynced).toLocaleDateString() : '—'}</span>
            </div>
          );
        })}
      </div>

      {selectedLead && (
        <LeadDetail leadId={selectedLead._id} onClose={() => { setSelectedLead(null); fetchLeads(); }} />
      )}
    </div>
  );
}

const s = {
  page: { padding: '1.5rem', fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 1150, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  title: { fontSize: 20, fontWeight: 600, margin: 0, color: '#1a1a1a' },
  sub: { fontSize: 13, color: '#888', marginTop: 4 },
  toolbar: { display: 'flex', gap: 8, marginBottom: 16 },
  search: { flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, background: '#fff', outline: 'none' },
  select: { padding: '8px 12px', borderRadius: 8, border: '1px solid #e0e0e0', fontSize: 13, background: '#fff', outline: 'none', minWidth: 140 },
  table: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 12, overflow: 'hidden' },
  tableHeader: { display: 'grid', gridTemplateColumns: '1.3fr 1.2fr 0.9fr 1fr 0.9fr 1fr 0.9fr', padding: '10px 16px', background: '#f5f4f0', fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', gap: 8 },
  tableRow: { display: 'grid', gridTemplateColumns: '1.3fr 1.2fr 0.9fr 1fr 0.9fr 1fr 0.9fr', padding: '12px 16px', borderTop: '1px solid #f0efe9', alignItems: 'center', gap: 8, cursor: 'pointer' },
  nameCell: { fontSize: 13, fontWeight: 600, color: '#1a1a1a', display: 'flex', alignItems: 'center' },
  cell: { fontSize: 13, color: '#555' },
  badge: { fontSize: 11, padding: '3px 10px', borderRadius: 10, fontWeight: 500, display: 'inline-block' },
  empty: { fontSize: 13, color: '#aaa', textAlign: 'center', padding: '2rem 0' },
};
